/** Resolve a short cardinal swipe to a grid neighbour from either direction.
 * Distances and cell bounds are CSS pixels. Subthreshold/ambiguous motion keeps
 * the source; remote drops and grid-edge crossings return -1 without wrapping. */
export function adjacentSwipeIndex(source, columns, count, dx, dy, rect) {
  const horizontal = Math.abs(dx) > Math.abs(dy) * 1.25;
  const vertical = Math.abs(dy) > Math.abs(dx) * 1.25;
  const distance = Math.max(Math.abs(dx), Math.abs(dy));
  const threshold = Math.max(8, Math.min(24, Math.min(rect.width, rect.height) * 0.3));
  if ((!horizontal && !vertical) || distance < threshold) return source;
  if (distance > Math.max(rect.width, rect.height)) return -1;
  const next = source + (horizontal ? Math.sign(dx) : Math.sign(dy) * columns);
  return next >= 0 &&
    next < count &&
    (!horizontal || Math.floor(next / columns) === Math.floor(source / columns))
    ? next
    : -1;
}

/** Cache stationary cell geometry for one captured drag and render only the
 * newest pointer sample per display frame. Board translation remains live so
 * page scrolling cannot shift a drop target. Resize cancels at the input owner. */
export function createDragSurface(board, fallback) {
  const view = board.ownerDocument?.defaultView ?? globalThis.window;
  let geometry = null,
    pending = null,
    frame = null;
  function begin() {
    const nodes = [...(board.querySelectorAll?.('[data-cell]') || [])];
    const origin = board.getBoundingClientRect?.();
    if (!nodes.length || !origin) return;
    geometry = nodes.map((node) => {
      const rect = node.getBoundingClientRect();
      return {
        index: Number(node.dataset.cell),
        left: rect.left - origin.left,
        right: rect.right - origin.left,
        top: rect.top - origin.top,
        bottom: rect.bottom - origin.top,
      };
    });
  }
  function index(event) {
    if (!geometry) {
      const nodes = board.querySelectorAll?.('[data-cell]');
      if (!nodes) return null;
      for (const node of nodes) {
        const r = node.getBoundingClientRect();
        if (
          event.clientX >= r.left &&
          event.clientX < r.right &&
          event.clientY >= r.top &&
          event.clientY < r.bottom
        )
          return Number(node.dataset.cell);
      }
      return -1;
    }
    const origin = board.getBoundingClientRect();
    const x = event.clientX - origin.left,
      y = event.clientY - origin.top;
    return (
      geometry.find((r) => x >= r.left && x < r.right && y >= r.top && y < r.bottom)?.index ?? -1
    );
  }
  function cancelFrame() {
    if (frame !== null) view?.cancelAnimationFrame(frame);
    frame = pending = null;
  }
  function flush() {
    if (frame !== null) view?.cancelAnimationFrame(frame);
    frame = null;
    const paint = pending;
    pending = null;
    paint?.();
  }
  return {
    begin,
    flush,
    indexAt(event) {
      if (!geometry) begin();
      return index(event) ?? fallback?.(event) ?? -1;
    },
    // A short swipe may end in its source or a grid gap. A remote cell remains
    // the actual drop target so callers can reject nonadjacent movement.
    swipeIndex(current, event, count) {
      const target = this.indexAt(event);
      if (target >= 0 && target !== current.index) return target;
      const rect = board.querySelector?.(`[data-cell="${current.index}"]`)?.getBoundingClientRect();
      return rect
        ? adjacentSwipeIndex(
            current.index,
            current.columns,
            count,
            event.clientX - current.x,
            event.clientY - current.y,
            rect,
          )
        : target;
    },
    carry(node, transform) {
      this.schedule(() => {
        if (node.isConnected !== false) node.style.transform = transform;
      });
    },
    index,
    schedule(callback) {
      if (!view?.requestAnimationFrame) return callback();
      pending = callback;
      if (frame !== null) return;
      frame = view.requestAnimationFrame(() => {
        frame = null;
        const paint = pending;
        pending = null;
        paint?.();
      });
    },
    cancelFrame,
    reset() {
      cancelFrame();
      geometry = null;
    },
  };
}
